import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { Bell, LogOut, ShieldCheck, User } from "lucide-react";
import { IconDotsVertical } from "@tabler/icons-react";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarRail,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNotificationStore } from "@/stores/notificationStore";
import { NotificationSettingsDialog } from "@/components/NotificationSettingsDialog";
import { getNavigationSections } from "@/lib/navigation";
import { getAvatarUrl } from "@/lib/utils";

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { unreadCount, fetchUnreadCount } = useNotificationStore();
  const [notificationsDialogOpen, setNotificationsDialogOpen] = useState(false);

  useEffect(() => {
    if (user) {
      void fetchUnreadCount();

      const interval = setInterval(() => {
        void fetchUnreadCount();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [user, fetchUnreadCount]);

  const navSections = useMemo(
    () => getNavigationSections(location.pathname),
    [location.pathname],
  );

  const handleLogout = async (): Promise<void> => {
    await supabase.auth.signOut();
    navigate("/login", { replace: true });
  };

  return (
    <Sidebar variant="inset" collapsible="icon">
      <SidebarHeader className="gap-3">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              className="cursor-default data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              disabled
            >
              <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                <ShieldCheck className="size-4" />
              </div>
              <div className="flex flex-col gap-0.5 leading-none text-left">
                <span className="font-semibold">AC Academy</span>
                <span className="text-xs text-muted-foreground">
                  Tanečný systém
                </span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {navSections.map((section) => (
          <SidebarGroup key={section.label}>
            <SidebarGroupLabel>{section.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {section.items.map((item) => (
                  <SidebarMenuItem key={item.url}>
                    <SidebarMenuButton
                      asChild
                      isActive={item.isActive}
                      tooltip={item.title}
                    >
                      {item.url.startsWith("#") ? (
                        <a href={item.url}>
                          <item.icon className="size-4" />
                          <span>{item.title}</span>
                        </a>
                      ) : (
                        <Link to={item.url}>
                          <item.icon className="size-4" />
                          <span>{item.title}</span>
                        </Link>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          {user && (
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                  >
                    {(() => {
                      const avatarUrl = getAvatarUrl(user.avatar_url);
                      const fallbackInitials =
                        user.display_name?.charAt(0).toUpperCase() ||
                        user.email?.charAt(0).toUpperCase() ||
                        "U";

                      return (
                        <div className="relative aspect-square size-8">
                          {avatarUrl ? (
                            <img
                              src={avatarUrl}
                              alt={user.display_name || "User"}
                              className="aspect-square size-8 rounded-lg object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = "none";
                                const parent = target.parentElement;
                                if (parent) {
                                  const fallback = parent.querySelector(
                                    ".avatar-fallback",
                                  ) as HTMLElement;
                                  if (fallback) {
                                    fallback.style.display = "flex";
                                  }
                                }
                              }}
                            />
                          ) : null}
                          <div
                            className={`flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground avatar-fallback ${
                              avatarUrl ? "absolute inset-0 hidden" : ""
                            }`}
                          >
                            <span className="text-sm font-semibold">
                              {fallbackInitials}
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-medium">
                        {user.display_name || "Používateľ"}
                      </span>
                      <span className="truncate text-xs text-muted-foreground">
                        {user.email}
                      </span>
                    </div>
                    <IconDotsVertical className="ml-auto size-4" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-[--radix-dropdown-menu-trigger-width] min-w-56 max-h-[calc(100vh-2rem)] overflow-y-auto rounded-lg"
                  side={isMobile ? "top" : "right"}
                  align={isMobile ? "start" : "end"}
                  sideOffset={isMobile ? 8 : 4}
                >
                  <DropdownMenuLabel className="p-0 font-normal">
                    <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                      {(() => {
                        const avatarUrl = getAvatarUrl(user.avatar_url);
                        const fallbackInitials =
                          user.display_name?.charAt(0).toUpperCase() ||
                          user.email?.charAt(0).toUpperCase() ||
                          "U";

                        return (
                          <div className="relative h-8 w-8">
                            {avatarUrl ? (
                              <img
                                src={avatarUrl}
                                alt={user.display_name || "User"}
                                className="h-8 w-8 rounded-lg object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = "none";
                                  const parent = target.parentElement;
                                  if (parent) {
                                    const fallback = parent.querySelector(
                                      ".avatar-fallback",
                                    ) as HTMLElement;
                                    if (fallback) {
                                      fallback.style.display = "flex";
                                    }
                                  }
                                }}
                              />
                            ) : null}
                            <div
                              className={`flex h-8 w-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground avatar-fallback ${
                                avatarUrl ? "absolute inset-0 hidden" : ""
                              }`}
                            >
                              <span className="text-sm font-semibold">
                                {fallbackInitials}
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                      <div className="grid flex-1 text-left text-sm leading-tight">
                        <span className="truncate font-medium">
                          {user.display_name || "Používateľ"}
                        </span>
                        <span className="truncate text-xs text-muted-foreground">
                          {user.email}
                        </span>
                      </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/user/account")}>
                    <User className="h-4 w-4" />
                    <span>Môj účet</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setNotificationsDialogOpen(true);
                    }}
                  >
                    <Bell className="h-4 w-4" />
                    <span>Nastavenia notifikácií</span>
                    {unreadCount > 0 && (
                      <Badge
                        variant="secondary"
                        className="ml-auto flex h-5 min-w-5 items-center justify-center px-1.5 text-xs"
                      >
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </Badge>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => navigate("/user/notifications")}
                  >
                    <Bell className="h-4 w-4" />
                    <span>Zobraziť všetky notifikácie</span>
                    {unreadCount > 0 && (
                      <Badge
                        variant="secondary"
                        className="ml-auto flex h-5 min-w-5 items-center justify-center px-1.5 text-xs"
                      >
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </Badge>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="h-4 w-4" />
                    <span>Odhlásiť sa</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          )}
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />

      <NotificationSettingsDialog
        open={notificationsDialogOpen}
        onOpenChange={setNotificationsDialogOpen}
      />
    </Sidebar>
  );
}
